/**
 * Страница установки PWA - инструкции для добавления на домашний экран
 * Адаптирована для tablet-pwa (активация через токен)
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './InstallPage.css'

export function InstallPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [platform, setPlatform] = useState<'ios' | 'android' | 'other'>('other')
  const [isInstalled, setIsInstalled] = useState(false)

  // Проверяем наличие activation token
  const activationToken = searchParams.get('token')
  const hasActivationToken = !!activationToken

  useEffect(() => {
    // Определяем платформу с улучшенной логикой
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || ''
    
    // Проверка iOS (более точная)
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream
    // Проверка Android (более точная)
    const isAndroid = /android/i.test(userAgent) && !isIOS
    
    if (isIOS) {
      setPlatform('ios')
    } else if (isAndroid) {
      setPlatform('android')
    } else {
      setPlatform('other')
    }

    // Если есть activation token, сохраняем его в localStorage для использования после установки PWA
    if (hasActivationToken) {
      console.log('[InstallPage] Saving activation token to localStorage')
      localStorage.setItem('pending_activation', JSON.stringify({
        token: activationToken,
        timestamp: Date.now()
      }))
    }

    // Проверяем, установлено ли приложение
    // Приоритет: 1) Native kiosk bridge (AndroidKiosk), 2) PWA display-mode, 3) iOS standalone
    const isStandalone = 
      !!(window as any).AndroidKiosk?.isStandalone?.() ||  // Native kiosk wrapper (addJavascriptInterface)
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (window.navigator as any).standalone === true ||
      (document.referrer ? document.referrer.includes('android-app://') : false)
    
    setIsInstalled(isStandalone)

    // Если приложение уже установлено и есть activation token, редиректим
    if (isStandalone && hasActivationToken) {
      console.log('[InstallPage] App installed, redirecting to activation')
      navigate(`/activate/${activationToken}`, { replace: true })
    }
  }, [hasActivationToken, activationToken, navigate])

  // Если приложение уже установлено без параметров activation, редиректим на главную
  if (isInstalled && !hasActivationToken) {
    navigate('/', { replace: true })
    return null
  }

  return (
    <div className="install-page dark-theme">
      <div className="install-page__container dark-theme">
        <div className="install-page__header">
          <div className="install-page__icon">📱</div>
          <h1 className="install-page__title">Установите RECVISIT</h1>
          <p className="install-page__subtitle">
            Добавьте приложение на домашний экран
          </p>
          <p className="install-page__note">
            После установки устройство активируется автоматически
          </p>
        </div>

        {platform === 'ios' && (
          <div className="install-page__instructions install-page__instructions--ios">
            <h2 className="install-page__instructions-title">
              Инструкция для iPhone/iPad
            </h2>
            <ol className="install-page__steps">
              <li className="install-page__step">
                <div className="install-page__step-icon">1️⃣</div>
                <div className="install-page__step-content">
                  <p>Нажмите кнопку <strong>"Поделиться"</strong> внизу экрана</p>
                  <div className="install-page__button-example">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 16.08C17.24 16.08 16.56 16.38 16.04 16.85L8.91 12.7C8.96 12.47 9 12.24 9 12C9 11.76 8.96 11.53 8.91 11.3L15.96 7.19C16.5 7.69 17.21 8 18 8C19.66 8 21 6.66 21 5C21 3.34 19.66 2 18 2C16.34 2 15 3.34 15 5C15 5.24 15.04 5.47 15.09 5.7L8.04 9.81C7.5 9.31 6.79 9 6 9C4.34 9 3 10.34 3 12C3 13.66 4.34 15 6 15C6.79 15 7.5 14.69 8.04 14.19L15.16 18.34C15.11 18.55 15.08 18.77 15.08 19C15.08 20.61 16.39 21.92 18 21.92C19.61 21.92 20.92 20.61 20.92 19C20.92 17.39 19.61 16.08 18 16.08Z" fill="currentColor"/>
                    </svg>
                    <span>Поделиться</span>
                  </div>
                </div>
              </li>
              <li className="install-page__step">
                <div className="install-page__step-icon">2️⃣</div>
                <div className="install-page__step-content">
                  <p>Прокрутите вниз и выберите <strong>"На экран «Домой»"</strong></p>
                  <div className="install-page__button-example install-page__button-example--secondary">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z" fill="currentColor"/>
                    </svg>
                    <span>На экран «Домой»</span>
                  </div>
                </div>
              </li>
              <li className="install-page__step">
                <div className="install-page__step-icon">3️⃣</div>
                <div className="install-page__step-content">
                  <p>Нажмите <strong>"Добавить"</strong> для подтверждения</p>
                </div>
              </li>
            </ol>
          </div>
        )}

        {platform === 'android' && (
          <div className="install-page__instructions install-page__instructions--android">
            <h2 className="install-page__instructions-title">
              Инструкция для Android
            </h2>
            <ol className="install-page__steps">
              <li className="install-page__step">
                <div className="install-page__step-icon">1️⃣</div>
                <div className="install-page__step-content">
                  <p>Нажмите кнопку <strong>"Меню"</strong> (три точки) в правом верхнем углу браузера</p>
                  <div className="install-page__button-example">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 8C13.1 8 14 7.1 14 6C14 4.9 13.1 4 12 4C10.9 4 10 4.9 10 6C10 7.1 10.9 8 12 8ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10ZM12 16C10.9 16 10 16.9 10 18C10 19.1 10.9 20 12 20C13.1 20 14 19.1 14 18C14 16.9 13.1 16 12 16Z" fill="currentColor"/>
                    </svg>
                    <span>Меню</span>
                  </div>
                </div>
              </li>
              <li className="install-page__step">
                <div className="install-page__step-icon">2️⃣</div>
                <div className="install-page__step-content">
                  <p>Выберите <strong>"Установить приложение"</strong> или <strong>"Добавить на главный экран"</strong></p>
                  <div className="install-page__button-example install-page__button-example--secondary">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 9H15V3H9V9H5L12 16L19 9Z" fill="currentColor"/>
                    </svg>
                    <span>Установить приложение</span>
                  </div>
                </div>
              </li>
              <li className="install-page__step">
                <div className="install-page__step-icon">3️⃣</div>
                <div className="install-page__step-content">
                  <p>Подтвердите установку, нажав <strong>"Установить"</strong> в диалоговом окне</p>
                </div>
              </li>
            </ol>
          </div>
        )}

        {platform === 'other' && (
          <div className="install-page__instructions install-page__instructions--other">
            <h2 className="install-page__instructions-title">
              Инструкция для установки
            </h2>
            <ol className="install-page__steps">
              <li className="install-page__step">
                <div className="install-page__step-icon">1️⃣</div>
                <div className="install-page__step-content">
                  <p>Найдите кнопку <strong>"Установить"</strong> или <strong>"Добавить на главный экран"</strong> в адресной строке браузера</p>
                </div>
              </li>
              <li className="install-page__step">
                <div className="install-page__step-icon">2️⃣</div>
                <div className="install-page__step-content">
                  <p>Нажмите на иконку установки и подтвердите действие</p>
                </div>
              </li>
              <li className="install-page__step">
                <div className="install-page__step-icon">3️⃣</div>
                <div className="install-page__step-content">
                  <p>Приложение будет добавлено на ваш домашний экран</p>
                </div>
              </li>
            </ol>
          </div>
        )}

        <div className="install-page__footer">
          <p className="install-page__footer-text">
            После установки приложение будет открываться как отдельное приложение
          </p>
        </div>
      </div>
    </div>
  )
}
